import React, { useEffect, useMemo, useRef, useState } from "react";
import { Order } from "../../class/Order";
import CustomPaper from "../ui/CutomPaper";
import AdronTypography from "../ui/AdronTypography";
import { Box, Paper, Stack } from "@mui/material";
import {
  ButtonStyled,
  IconButtonPaper,
  NumericFormatCustom,
  TextFieldStyled,
} from "../ui/StyledAdron";
import { _roundDec, _roundToDecimals } from "../../utils/_roundToDecimals";
import { Bot, Colors, Direction } from "../../_app/types";
import { precision } from "../../utils/_precision";
import IconComponent from "../ui/IconComponent";
import SwitchField from "../ui/inputs/SwitchField";
import { v4 } from "uuid";
import { useTheme } from "@mui/material/styles";
import { _adjustInvestment } from "../../utils/_adjustInvestment";
import { useDispatch, useSelector } from "react-redux";
import { setCustomGridMode } from "../../store/slices/customBotSlice";
import { showNotification } from "../../store/slices/notificationSlice";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  setCustomGridSettings,
  setInvestment,
  setMaxPrice,
  setMinPrice,
} from "../../store/slices/botSlice";
import { RootState } from "../../store/store";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import Spacer from "../../_components/Spacer";
import { useAppNavigate } from "../../_hooks/use-app-navigate";

interface Props {
  orders: Order[];
  bot: Bot;
  showAdvanced: (value: boolean) => void;
  stepInBase: number;
  stepInQuote: number;
}

const AdvancedGridSettings = ({ orders, bot, showAdvanced, stepInBase, stepInQuote }: Props) => {
  const theme = useTheme();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const endOfListRef = useRef<HTMLDivElement | null>(null);
  const [isNewItemAdded, setIsNewItemAdded] = useState(false);
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);

  const maxPrice = useMemo(() => {
    let allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.max(...allPrices);
  }, [bot.customGridSettings]);

  const minPrice = useMemo(() => {
    let allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.min(...allPrices);
  }, [bot.customGridSettings]);

  const minOrderAmount = useMemo(() => {
    return Math.min(...bot.customGridSettings.flatMap((item) => item.amount));
  }, [bot.customGridSettings]);

  const saveCustomGrid = () => {
    dispatch(setCustomGridMode(true));
    dispatch(setMinPrice(minPrice));
    dispatch(setMaxPrice(maxPrice));
    showAdvanced(false);
    dispatch(
      showNotification({
        message: `Custom grid mode is enabled`,
        severity: "info",
      })
    );
    if (CustomGridMode) {
      // dispatch(setCurrencyType('BASE'));
      // let newInvestment = 0;
      // // if(bot.currencyType === 'BASE') {
      // // 	newInvestment = orders.reduce((sum, order) => {
      // // 		return sum + order.amount;
      // // 	}, 0);
      // // } else {
      // // 	newInvestment = orders.reduce((sum, order) => {
      // // 		return sum + (order.amount * order.openPrice);
      // // 	}, 0);
      // // }
      // newInvestment = bot.customGridSettings.reduce((sum, order) => {
      // 	return sum + order.amount;
      // }, 0);
      //
      // let investmentStepInQuoteNew = orders.reduce((sum, order) => {
      // 	return sum + bot.ticker.orderStep;
      // }, 0);
      //
      // dispatch(setInvestment(
      // 	_adjustInvestment(newInvestment, investmentStepInQuoteNew),
      // ));
      // console.log(_adjustInvestment(newInvestment, investmentStepInQuoteNew));
    }
  };

  const addGridItem = () => {
    const newOrder = {
      orderSide: "Short",
      openPrice: bot.ticker.priceStep * 1000 + maxPrice,
      closePrice: maxPrice,
      amount: minOrderAmount,
      settingId: v4(),
      doNotSubstitute: false,
    };
    dispatch(setCustomGridSettings([...bot.customGridSettings, newOrder]));
    setIsNewItemAdded(true);
  };

  const deleteItem = (index: number) => {
    let customOrders = [...bot.customGridSettings];
    customOrders.splice(index, 1);
    dispatch(setCustomGridSettings(customOrders));
  };

  const handleOpenPriceChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +event.target.value;
    const updatedGrid = bot.customGridSettings.map((order, idx) =>
      idx === index ? { ...order, openPrice: newValue } : order
    );
    dispatch(setCustomGridSettings(updatedGrid));
  };
  const handleClosePriceChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = +event.target.value;
      const updatedGrid = bot.customGridSettings.map((order, idx) =>
        idx === index ? { ...order, closePrice: newValue } : order
      );
      dispatch(setCustomGridSettings(updatedGrid));
    };
  const handleAmountChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +event.target.value;
    const updatedGrid = bot.customGridSettings.map((order, idx) =>
      idx === index ? { ...order, amount: newValue } : order
    );
    dispatch(setCustomGridSettings(updatedGrid));
  };
  const handleDirectionChange = (index: number) => (newValue: any) => {
    const updatedGrid = bot.customGridSettings.map((order, idx) =>
      idx === index ? { ...order, orderSide: newValue } : order
    );
    dispatch(setCustomGridSettings(updatedGrid));
  };

  useEffect(() => {
    // dynamic calc investment for all custom orders
    CustomGridMode &&
      dispatch(
        setInvestment(
          bot.customGridSettings.reduce((sum, order) => {
            return sum + order.amount * order.openPrice;
          }, 0)
        )
      );
  }, [bot.customGridSettings, CustomGridMode]);

  useEffect(() => {
    if (!CustomGridMode) {
      const sortOrders = orders.sort((a, b) => a.openPrice - b.openPrice);
      const newGrid = sortOrders.map((order) => ({
        openPrice: _roundDec(order.openPrice, precision(bot.ticker.priceStep)),
        closePrice: _roundDec(order.closePrice, precision(bot.ticker.priceStep)),
        amount:
          bot.stepsCount > 0 && bot.stepsCount !== Infinity
            ? order.amount * _roundDec(bot.stepsCount, 0)
            : order.amount,
        orderSide: order.orderSide,
        settingId: v4(),
        doNotSubstitute: false,
      }));
      dispatch(setCustomGridSettings(newGrid));
    }
  }, []);

  useEffect(() => {
    if (isNewItemAdded && endOfListRef.current) {
      endOfListRef.current.scrollIntoView({ behavior: "smooth" });
      setIsNewItemAdded(false);
    }
  }, [isNewItemAdded]);

  useEffect(() => {
    console.log(bot.stepsCount);
  }, [bot.stepsCount]);

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: "1000",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        background:
          theme.palette.mode === "dark" ? "rgba(13, 16, 18, 1)" : "rgba(245, 245, 245, 1)",
      }}
    >
      <PageLayout
        header={<Header pageTitle={"Futures bot preview"} needBack onChange={showAdvanced} />}
        bottomButton={
          <Paper
            elevation={0}
            className="base-adron-paper paper-light"
            style={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0px -4px 6px -1px rgba(0, 0, 0, 0.10",
            }}
          >
            <Stack direction={"column"} spacing={1}>
              <ButtonStyled
                onClick={addGridItem}
                style={{ padding: "8px 16px", color: "#fff", fontWeight: 600, height: "40px" }}
                fullWidth={true}
                color={"secondary"}
                endIcon={<IconComponent iconName={"Plus"} hasFill hasStroke />}
                variant="contained"
              >
                Add grid
              </ButtonStyled>
              <ButtonStyled
                onClick={saveCustomGrid}
                style={{ padding: "8px 16px", color: "#fff", fontWeight: 600, height: "40px" }}
                fullWidth={true}
                color={"primary"}
                variant="contained"
              >
                Save
              </ButtonStyled>
            </Stack>
          </Paper>
        }
        contentSx={{ padding: "20px 8px 0" }}
      >
        <Stack
          sx={{ width: "100%", marginBottom: 2 }}
          direction={"row"}
          justifyContent={"space-between"}
        >
          <AdronTypography halfOpacity value={"Investment"} />
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <AdronTypography
              fontSize={20}
              style={{ fontWeight: 600 }}
              value={_roundDec(
                _adjustInvestment(
                  bot.investment,
                  bot.currencyType === "BASE" ? stepInBase : stepInQuote
                ),
                precision(bot.ticker.orderStep)
              )}
            />
            <IconComponent
              type={"asBackground"}
              iconName={`${bot.currencyType === "BASE" ? bot.base : bot.quote}`}
              size={22}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} spacing={1} sx={{ width: "100%" }}>
          {bot.customGridSettings.map((order, index) => (
            <CustomPaper key={index}>
              <Stack direction={"column"} spacing={"20px"}>
                <Stack
                  sx={{ width: "100%" }}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <AdronTypography style={{ fontWeight: 600 }} value={`Grid ${index + 1}`} />
                  <SwitchField
                    value={order.orderSide as Direction}
                    onChange={handleDirectionChange(index)}
                    paramList={[
                      { label: "Short", icon: <IconComponent iconName={"TradeDown"} hasFill /> },
                      { label: "Long", icon: <IconComponent iconName={"TradeUp"} hasFill /> },
                    ]}
                  />
                </Stack>
                <Stack direction={"row"} spacing={1}>
                  <TextFieldStyled
                    className={"AdronRangePrice"}
                    fullWidth={true}
                    label="Open price"
                    variant="outlined"
                    value={
                      _roundToDecimals(order.openPrice, precision(bot.ticker.priceStep)) === 0
                        ? ""
                        : _roundToDecimals(order.openPrice, precision(bot.ticker.priceStep))
                    }
                    size="small"
                    type={"text"}
                    onChange={handleOpenPriceChange(index)}
                    InputProps={{
                      style: { textAlign: "right" },
                      endAdornment: (
                        <AdronTypography
                          style={{ marginLeft: "6px" }}
                          halfOpacity
                          value={`${bot.quote}`}
                        />
                      ),
                      inputComponent: NumericFormatCustom as any,
                      inputProps: {
                        inputMode: "decimal",
                        pattern: "[0-9]*",
                        decimalCustom: precision(bot.ticker.priceStep),
                      },
                    }}
                  />
                  <TextFieldStyled
                    className={"AdronRangePrice"}
                    fullWidth={true}
                    label="Close price"
                    variant="outlined"
                    value={
                      _roundToDecimals(order.closePrice, precision(bot.ticker.priceStep)) === 0
                        ? ""
                        : _roundToDecimals(order.closePrice, precision(bot.ticker.priceStep))
                    }
                    size="small"
                    type={"text"}
                    onChange={handleClosePriceChange(index)}
                    InputProps={{
                      endAdornment: (
                        <AdronTypography
                          style={{ marginLeft: "6px" }}
                          halfOpacity
                          value={`${bot.quote}`}
                        />
                      ),
                      inputComponent: NumericFormatCustom as any,
                      inputProps: {
                        inputMode: "decimal",
                        pattern: "[0-9]*",
                        decimalCustom: precision(bot.ticker.priceStep),
                      },
                    }}
                  />
                </Stack>
                <Stack direction={"row"} spacing={1}>
                  <TextFieldStyled
                    className={"AdronRangePrice"}
                    fullWidth={true}
                    label="Amount"
                    variant="outlined"
                    value={order.amount === 0 ? "" : order.amount}
                    size="small"
                    type={"text"}
                    onChange={handleAmountChange(index)}
                    InputProps={{
                      style: { textAlign: "right" },
                      endAdornment: (
                        <AdronTypography
                          style={{ marginLeft: "6px" }}
                          halfOpacity
                          value={`${bot.base}`}
                        />
                      ),
                      inputComponent: NumericFormatCustom as any,
                      inputProps: {
                        inputMode: "decimal",
                        pattern: "[0-9]*",
                        decimalCustom: precision(bot.ticker.orderStep),
                      },
                    }}
                  />
                  <IconButtonPaper
                    onClick={() => deleteItem(index)}
                    sx={{ backgroundColor: Colors.Destructive }}
                  >
                    <DeleteOutlineIcon />
                  </IconButtonPaper>
                  {/*<ButtonStyled*/}
                  {/*	onClick={() => deleteItem(index)}*/}
                  {/*	style={{padding: "8px 16px", backgroundColor: Colors.Destructive, color: "#fff", fontWeight: 600, height: "40px"}}*/}
                  {/*	fullWidth={true}*/}
                  {/*	color={"error"}*/}
                  {/*	variant="contained"*/}
                  {/*>*/}
                  {/*	Delete*/}
                  {/*</ButtonStyled>*/}
                </Stack>
              </Stack>
            </CustomPaper>
          ))}
          <div ref={endOfListRef} />
        </Stack>
        <Spacer height="145px" />
      </PageLayout>
    </Box>
  );
};

export default AdvancedGridSettings;
